import * as PIXI from 'pixi.js';
import { Application, Loader, utils } from 'pixi.js';
import map from '@/assets/map';
import eventHub from '@/scripts/eventHub';
import gsap from 'gsap/gsap-core';
import audio from '@/scripts/audio';
import store from '@/store';
import { inApp, getLang } from '@/scripts/utils';
import IntroStage from './stages/IntroStage';
import BackgroundStage from './stages/BackgroundStage';
import MainStage from './stages/MainStage';
import Floor from './actors/Floor';
import Player from './actors/Player';
import LightRay from './actors/LightRay';
import InteractionManager from './interaction';
import HitArea from './utils/HitArea';
import PowerDownArea from './actors/PowerDownArea';
import PowerUpArea from './actors/PowerUpArea';
import Background from './actors/Background';
import DifficultyManager from './utils/DifficultyManager';
import SideRails from './actors/SideRails';
import state from './state';

class Bloom {
  constructor() {
    this.app = null;
    this.assets = map;
    this.DOMFrame = null;
    this.DOMCanvas = null;
    this.DOMRect = null;

    this.stopped = true;
    this.shareLoaded = false;
    this.introTicker = new PIXI.Ticker();

    this.backgroundStage = null;
    this.mainStage = null;
    this.floor = null;
    this.player = null;
    this.lightRay = null;
    this.interaction = null;
    this.difficulty = null;
  }

  init(DOMFrame, DOMCanvas) {
    this.DOMFrame = DOMFrame;
    this.DOMCanvas = DOMCanvas;

    utils.skipHello();
    this.app = new Application({
      view: this.DOMCanvas,
      autoDensity: false,
      autoStart: false,
      transparent: true,
      antialias: true,
      sharedLoader: true,
      sharedTicker: true,
    });
    this.app.stage.sortableChildren = true;

    this.resize();
    this.setUpIntro();

    this.app.render();
    this.app.stop();

    // Event management
    eventHub.$on('play-pause', () => { this.togglePlayPlause(); });
    eventHub.$on('konami-code', () => { this.applyPostProcessing(); });
    eventHub.$on('shake', () => { this.shakeStage(); });
    eventHub.$on('start-spawning', () => { this.startSpawning(); });
  }

  load() {
    global.PIXI = PIXI;
    const sectionKeys = Object.keys(this.assets);
    sectionKeys.forEach((sectionKey) => {
      const assetKeys = Object.keys(this.assets[sectionKey]);
      assetKeys.forEach((assetKey) => {
        Loader.shared.add(`${sectionKey}/${assetKey}`, this.assets[sectionKey][assetKey].url);
      });
    });
    const assets = new Promise((resolve) => { Loader.shared.load(resolve); });
    const projectionLib = import('@/assets/libs/pixi-projection');
    return Promise.all([assets, projectionLib]);
  }

  start() {
    this.app.start();
    this.stopped = false;
    state.stopped = false;
  }

  pause() {
    this.app.stop();
    this.stopped = true;
    state.stopped = true;
  }

  togglePlayPlause() {
    if (this.stopped) {
      this.start();
    } else {
      this.pause();
    }
  }

  resize() {
    // this.DOMRect = this.DOMFrame.getBoundingClientRect();
    this.app.view.width = 375;
    this.app.view.height = 812;
  }

  setUpIntro() {
    this.introStage = new IntroStage(this.app.stage);
    this.introStage.ref.zIndex = 2;
    this.app.stage.sortChildren();
    this.introTicker.add(() => { this.app.render(); });
    this.introTicker.start();
  }

  goToCharSelection() {
    this.introStage.firstReveal();
  }

  async backToCharSelection() {
    this.introTicker.start();
    await this.introStage.midRemove();
    this.stopPlaying();
    this.introStage.finalRemove();
  }

  async setUpGame() {
    // Wait for courtains to be closed
    await this.introStage.midReveal();

    // Initialize the front stage and the background stage
    this.backgroundStage = new BackgroundStage(this.app.stage);
    this.mainStage = new MainStage(this.app.stage);

    // Init all the actors
    this.hitArea = new HitArea(this.mainStage);
    this.floor = new Floor(this.mainStage, this.hitArea, this.app.renderer);
    this.background = new Background(this.mainStage);
    this.sideRails = new SideRails(this.mainStage);
    this.lightRay = new LightRay(this.mainStage, this.hitArea, this.app.renderer);
    this.player = new Player(this.mainStage);
    this.powerDownArea = new PowerDownArea(this.mainStage);
    this.powerUpArea = new PowerUpArea(this.mainStage, this.app.renderer);

    // Init the interaction manger
    this.interaction = new InteractionManager(this.DOMFrame);
    this.difficulty = new DifficultyManager();
  }

  async startPlaying() {
    this.introTicker.stop();
    this.player.spawn();
    this.interaction.start();
    this.lightRay.bindRotation(this.interaction);
    this.hitArea.bindRotation(this.interaction);
    this.lightRay.bindSwitch(this.interaction);

    // Then open courtains
    await this.introStage.finalReveal();

    audio.play('background');
    audio.play('background-walk');
  }

  startSpawning() {
    this.floor.startSpawning();
  }

  gameover() {
    this.pause();
    state.gameover = true;
    audio.play('gameover');
  }

  stopPlaying() {
    audio.stop('background');
    audio.stop('background-walk');

    // First stop various binders and tickers
    this.player.unspawn();
    this.interaction.stop();
    this.lightRay.unbindRotation();
    this.hitArea.unbindRotation();
    this.lightRay.unbindSwitch(this.interaction);
    this.floor.stopSpawning();

    // Reset the game state
    state.reset();

    // Destroy all the game actors
    this.difficulty.destroy();
    this.difficulty = null;
    this.interaction.destroy();
    this.interaction = null;
    this.powerUpArea.destroy();
    this.powerUpArea = null;
    this.powerDownArea.destroy();
    this.powerDownArea = null;
    this.player.destroy();
    this.player = null;
    this.lightRay.destroy();
    this.lightRay = null;
    this.sideRails.destroy();
    this.sideRails = null;
    this.background.destroy();
    this.background = null;
    this.floor.destroy();
    this.floor = null;
    this.hitArea.destroy();
    this.hitArea = null;
    this.mainStage.destroy();
    this.mainStage = null;
    this.backgroundStage.destroy();
    this.backgroundStage = null;

    // Stop the app ticker
    this.app.stop();
  }

  async applyPostProcessing() {
    if (this.app.stage.filters !== null) {
      this.app.stage.filters = null;
    } else {
      const pixelateFilter = import('@pixi/filter-pixelate');
      const crtFilter = import('@pixi/filter-crt');
      await Promise.all([pixelateFilter, crtFilter]).then(([pFilter, cFilter]) => {
        this.app.stage.filters = [
          new pFilter.PixelateFilter(3, 3),
          new cFilter.CRTFilter({
            vignettingAlpha: 0.6,
          }),
        ];
        this.app.render();
      });
    }
  }

  shakeStage() {
    const tl = gsap.timeline();
    tl.to(this.app.stage.position, { x: 5, duration: 0.05, ease: 'linear.none' });
    tl.to(this.app.stage.position, { x: -5, duration: 0.05, ease: 'linear.none' });
    tl.to(this.app.stage.position, { x: 5, duration: 0.05, ease: 'linear.none' });
    tl.to(this.app.stage.position, { x: -5, duration: 0.05, ease: 'linear.none' });
    tl.to(this.app.stage.position, { x: 0, duration: 0.05, ease: 'linear.none' });
  }

  async generateShareImage() {
    // Standalone function for generating share image
    const sharepath = `/assets/share/${store.state.isGameOver ? store.state.characterId : 'nochar'}/${inApp() ? 'app' : 'desktop'}/${getLang()}/share.jpg`;
    const baseImage = PIXI.Sprite.from(sharepath);
    const p = new Promise((resolve) => {
      if (this.shareLoaded) resolve();
      else {
        baseImage.texture.baseTexture.on('loaded', resolve);
        this.shareLoaded = true;
      }
    });
    await p;
    const text = new PIXI.Text(store.state.isGameOver ? state.points.toString() : store.state.userData.score, {
      fill: 0x883832,
      fontSize: 124,
      fontFamily: 'Granjon',
      fontWeight: 700,
    });
    text.anchor = new PIXI.Point(0.5, 0.5);
    text.position.x = baseImage.width / 2;
    text.position.y = 720;
    baseImage.addChild(text);
    const b64 = this.app.renderer.plugins.extract.base64(baseImage, 'image/jpg', 0.6);
    return b64;
  }
}

export default Bloom;
